import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, UserCog } from "lucide-react";
import api from "../lib/api";

const ROLES = ["procurement_manager", "procurement_specialist", "sales", "finance", "logistics"];
const roleLabel = (r) => r.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function Team() {
  const [items, setItems] = useState(null);
  const [adding, setAdding] = useState(false);
  const [forbidden, setForbidden] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/staff").then((r) => setItems(r.data)).catch((e) => {
      if (e?.response?.status === 403) setForbidden(true);
      else setItems([]);
    });
  }, []);
  useEffect(() => { load(); }, [load]);

  const setRole = async (u, role) => {
    try { await api.patch(`/admin/staff/${u.id}`, { role }); toast.success(`${u.name} is now ${roleLabel(role)}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const toggleActive = async (u) => {
    try { await api.patch(`/admin/staff/${u.id}`, { active: !(u.active !== false) }); toast.success(u.active !== false ? `${u.name} deactivated` : `${u.name} reactivated`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.name} (${u.email}) from the team? This cannot be undone.`)) return;
    try { await api.delete(`/admin/staff/${u.id}`); toast.success("Team member removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  if (forbidden) {
    return (
      <div data-testid="team-forbidden" className="bg-white rounded-xl border border-slate-200 p-10 max-w-lg text-center">
        <UserCog className="mx-auto text-slate-300" size={40} />
        <p className="mt-4 text-[14.5px] text-slate-500">Only the super admin can manage team members.</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-team-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-[Poppins] font-bold text-[26px] text-[var(--pg-navy)]">Team Members</h1>
        <button data-testid="add-staff-button" onClick={() => setAdding(true)} className="btn-primary !py-2.5 !px-4 text-[13px]"><Plus size={15} /> Add Team Member</button>
      </div>
      <p className="mt-2 text-[13.5px] text-slate-500">Team members get full admin dashboard access. Only you (super admin) can manage this list.</p>

      {adding && <StaffForm onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}

      <div className="mt-5 bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-left text-[13.5px] min-w-[760px]">
          <thead>
            <tr className="border-b border-slate-200 text-[11.5px] uppercase tracking-wider text-slate-500 font-[Poppins]">
              <th className="px-5 py-3">Name</th><th className="px-5 py-3">Email</th><th className="px-5 py-3">Role</th>
              <th className="px-5 py-3">Status</th><th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items === null && <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-400"><Loader2 className="animate-spin inline" size={18} /></td></tr>}
            {items && items.length === 0 && <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-400" data-testid="team-empty-state">No team members yet.</td></tr>}
            {items && items.map((u) => {
              const isSuper = u.role === "super_admin";
              const active = u.active !== false;
              return (
                <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`staff-row-${u.email}`}>
                  <td className="px-5 py-3.5 font-[Poppins] font-semibold text-[var(--pg-navy)]">{u.name}</td>
                  <td className="px-5 py-3.5 text-slate-500">{u.email}</td>
                  <td className="px-5 py-3.5">
                    {isSuper ? (
                      <span className="px-2.5 py-1 rounded-full text-[11px] font-[Poppins] font-semibold bg-[var(--pg-navy)] text-white">Super Admin</span>
                    ) : (
                      <select data-testid={`staff-role-select-${u.email}`} className="pg-input !py-1.5 !px-2 !w-[190px] text-[13px]" value={u.role} onChange={(e) => setRole(u, e.target.value)}>
                        {ROLES.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
                      </select>
                    )}
                  </td>
                  <td className="px-5 py-3.5">
                    <span className={`px-2.5 py-1 rounded-full text-[11px] font-[Poppins] font-semibold ${active ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-500"}`}>
                      {active ? "ACTIVE" : "DEACTIVATED"}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 whitespace-nowrap text-right">
                    {!isSuper && (
                      <>
                        <button data-testid={`staff-toggle-${u.email}`} onClick={() => toggleActive(u)}
                          className={`text-[11.5px] font-[Poppins] font-semibold px-3 py-1.5 rounded-lg ${active ? "bg-amber-100 text-amber-700 hover:bg-amber-200" : "bg-green-100 text-green-700 hover:bg-green-200"}`}>
                          {active ? "Deactivate" : "Reactivate"}
                        </button>
                        <button data-testid={`staff-delete-${u.email}`} onClick={() => remove(u)} title="Remove" className="ml-1 text-slate-500 hover:text-red-600 p-1.5" aria-label="Remove team member"><Trash2 size={16} /></button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StaffForm({ onClose, onSaved }) {
  const [f, setF] = useState({ name: "", email: "", password: "", role: "sales" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const h = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try { await api.post("/admin/staff", f); toast.success(`${f.name} added to the team`); onSaved(); }
    catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Could not create the account. Check the fields and try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4" onClick={onClose}>
      <form data-testid="staff-form" onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-white rounded-2xl p-8 shadow-2xl">
        <h2 className="font-[Poppins] font-bold text-[19px] text-[var(--pg-navy)]">Add Team Member</h2>
        <div className="mt-5 space-y-4">
          <div><label className="pg-label">Full Name *</label><input data-testid="staff-name-input" className="pg-input" value={f.name} onChange={h("name")} required minLength={2} /></div>
          <div><label className="pg-label">Email *</label><input data-testid="staff-email-input" type="email" className="pg-input" value={f.email} onChange={h("email")} required /></div>
          <div><label className="pg-label">Temporary Password * <span className="text-slate-400 font-normal">(min 8 characters)</span></label>
            <input data-testid="staff-password-input" type="text" className="pg-input" value={f.password} onChange={h("password")} required minLength={8} /></div>
          <div><label className="pg-label">Role *</label>
            <select data-testid="staff-role-input" className="pg-input" value={f.role} onChange={h("role")}>
              {ROLES.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
            </select></div>
        </div>
        {error && <p data-testid="staff-form-error" className="mt-4 text-[13.5px] text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>}
        <div className="mt-6 flex gap-3">
          <button data-testid="staff-form-submit" type="submit" disabled={busy} className="btn-primary flex-1 justify-center disabled:opacity-70">
            {busy ? <Loader2 className="animate-spin" size={17} /> : "Create Account"}
          </button>
          <button type="button" data-testid="staff-form-cancel" onClick={onClose} className="px-5 rounded-lg border border-slate-200 text-[13.5px] font-[Poppins] font-semibold text-slate-500 hover:border-slate-400">Cancel</button>
        </div>
      </form>
    </div>
  );
}
